// Mouse-speed "intent" detector for Overfladedetektion (SurfaceDetection.js).
//
// While the mouse sweeps quickly across the screen the user is moving, not
// aiming, so no surface analysis (GPU pick + PCA) is wanted and a disc chasing
// the pointer is only noise. When the movement slows to what precise
// positioning looks like - or stops - the detection should come back at once.
//
// Pure (no DOM/THREE/clock): the caller passes explicit timestamps, so it is
// Level 1 testable with synthetic trajectories - see MouseIntent.test.js.
//
// The measure is SCREEN speed (px/s of the pointer), so it does not depend on
// camera distance or the scale of the point cloud.
//
// Speed estimate: net displacement of the pointer over the last `windowMs`
// milliseconds divided by the elapsed time (net displacement, not path length:
// +-1 px hand/sensor jitter around a point must not read as movement). No
// event for `stopMs` means the pointer is at rest (speed 0) - a mouse sends no
// events when it stands still, so "stopped" has to be derived from time.
//
// Hysteresis (the one mechanism against flicker at a single threshold):
//   - enter FAST as soon as speed >= hiPxPerS
//   - leave FAST only when speed <= loPxPerS has held for holdMs (or the
//     pointer has stopped), so speed wobbling between the two thresholds
//     changes nothing.
//
// PROVISIONAL thresholds - chosen from simulated human pointing movements
// (minimum-jerk sweeps, corrective sub-movements, dwell; see the report and
// MouseIntent.test.js) and verified in the live viewer; not product rules.

export const INTENT_DEFAULTS = {
	windowMs: 60,
	stopMs: 35,
	hiPxPerS: 800,
	loPxPerS: 500,
	holdMs: 30,
};

const CAPACITY = 16;

export class MouseIntent {
	constructor(params = {}){
		this.params = Object.assign({}, INTENT_DEFAULTS, params);
		this.xs = new Float64Array(CAPACITY);
		this.ys = new Float64Array(CAPACITY);
		this.ts = new Float64Array(CAPACITY);
		this.n = 0;            // number of buffered events
		this.fast = false;
		this.belowSince = -1;  // time the speed first dropped to <= lo (or -1)
		this.speedPxPerS = 0;
	}

	reset(){
		this.n = 0; this.fast = false; this.belowSince = -1; this.speedPxPerS = 0;
	}

	// record one raw mousemove (t in ms, monotonically increasing)
	push(x, y, t){
		if(this.n === CAPACITY){
			for(let i = 1; i < CAPACITY; i++){ this.xs[i - 1] = this.xs[i]; this.ys[i - 1] = this.ys[i]; this.ts[i - 1] = this.ts[i]; }
			this.n--;
		}
		this.xs[this.n] = x; this.ys[this.n] = y; this.ts[this.n] = t;
		this.n++;
	}

	// pointer speed (px/s) at time t from the buffered events
	speed(t){
		const p = this.params;
		if(this.n === 0) return 0;
		const newest = this.n - 1;
		if(t - this.ts[newest] > p.stopMs) return 0; // no events: at rest

		// oldest event still inside the window (but keep >= 1 event)
		let first = newest;
		while(first > 0 && this.ts[newest] - this.ts[first - 1] <= p.windowMs) first--;
		if(first === newest) return this.speedPxPerS; // a single event: no new information
		const dt = this.ts[newest] - this.ts[first];
		if(dt < 4) return this.speedPxPerS;            // two events (almost) at once: unreliable
		const dx = this.xs[newest] - this.xs[first], dy = this.ys[newest] - this.ys[first];
		return Math.hypot(dx, dy) / dt * 1000;
	}

	// advance the state machine to time t; returns {fast, speed, resumed}
	// `resumed` is true on exactly the evaluation where FAST ends.
	update(t){
		const p = this.params;
		const s = this.speed(t);
		this.speedPxPerS = s;
		let resumed = false;

		if(!this.fast){
			if(s >= p.hiPxPerS){ this.fast = true; this.belowSince = -1; }
		}else{
			const stopped = this.n === 0 || (t - this.ts[this.n - 1] > p.stopMs);
			if(s <= p.loPxPerS){
				if(this.belowSince < 0) this.belowSince = t;
				if(stopped || t - this.belowSince >= p.holdMs){
					this.fast = false; this.belowSince = -1; resumed = true;
				}
			}else{
				this.belowSince = -1;
			}
		}
		return {fast: this.fast, speed: s, resumed};
	}
}
